// react
import {
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
  useCallback,
} from "react";
import { useTranslation } from "react-i18next";
// node-modules
import axios, { AxiosResponse } from "axios";
// mui
import {
  DialogActions,
  Button,
  useTheme,
  styled,
  DialogContentText,
  Box,
  Popover,
} from "@mui/material";
// src
import { config } from "../config/config";
import { LoadingButton } from "@mui/lab";
import { FolderZip } from "@mui/icons-material";
import { Common } from "../../common/src";
import { ColorResult, SketchPicker } from "react-color";
import JSZip from "jszip";
import { formatFileSize, isXML } from "../utils/file";
import { reUploadARFile } from "../api/project";

// Types
type Props = {
  projectID: string;
  projectName: string;
  setProjectName?: Dispatch<SetStateAction<string>>;
  openDialog: boolean;
  setOpenDialog?: Dispatch<SetStateAction<boolean>>;
  jobSiteId: string;
  setJobSiteId?: Dispatch<SetStateAction<string>>;
  setErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  setErrorMsg: Dispatch<SetStateAction<string>>;
  setOpenWarning: Dispatch<SetStateAction<boolean>>;
  setMsgWarning: Dispatch<SetStateAction<string>>;
  setProgressDialog: Dispatch<SetStateAction<boolean>>;
  setUploadResult: Dispatch<SetStateAction<boolean>>;
  setUpLoadingPercent: Dispatch<SetStateAction<number>>;
  selectType: number;
  ar_color: string;
};

type FileState = {
  file?: File;
  fileName: string;
  fileSize: string;
  selected: boolean;
};

const FILE_ACCEPTS = {
  1: ".ifc,.xml",
  2: ".las",
  3: ".tif,.tiff",
  4: ".json",
  xml: ".xml",
} as const;

const UploadARDataDialog = ({
  projectID,
  projectName,
  setProjectName,
  openDialog,
  setOpenDialog,
  jobSiteId,
  setJobSiteId,
  setErrorSnackbar,
  setErrorMsg,
  setOpenWarning,
  setMsgWarning,
  setProgressDialog,
  setUploadResult,
  setUpLoadingPercent,
  selectType,
  ar_color,
}: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // State consolidation
  const [loading, setLoading] = useState({
    upload: false,
    file: false,
  });

  const [fileStates, setFileStates] = useState({
    design: {
      file: undefined,
      fileName: "",
      fileSize: "",
      selected: false,
    } as FileState,
    las: {
      file: undefined,
      fileName: "",
      fileSize: "",
      selected: false,
    } as FileState,
    tif: {
      file: undefined,
      fileName: "",
      fileSize: "",
      selected: false,
    } as FileState,
    xml: {
      file: undefined,
      fileName: "",
      fileSize: "",
      selected: false,
    } as FileState,
    json: {
      file: undefined,
      fileName: "",
      fileSize: "",
      selected: false,
    } as FileState,
  });

  const [validation, setValidation] = useState({
    projectName: true,
    upload: true,
    isValid: true,
  });

  const [colorPicker, setColorPicker] = useState({
    color: ar_color || "#50E3C2",
    open: false,
    anchorEl: null as HTMLElement | null,
  });

  const [jobsiteList, setJobsiteList] = useState<Common.LandlogJobSite[]>([]);
  const [timeStamp, setTimeStamp] = useState<number>(0);
  const [useArmarker, setUseArmarker] = useState<string>("false");

  const updateFileState = useCallback(
    (type: keyof typeof fileStates, file: File) => {
      setFileStates((prev) => ({
        ...prev,
        [type]: {
          file,
          fileName: file.name,
          fileSize: formatFileSize(file.size),
          selected: true,
        },
      }));
    },
    [formatFileSize]
  );

  // Validation logic
  const validateUpload = useCallback(() => {
    const { design, las, tif, xml, json } = fileStates;

    const isValidSelect3 = selectType === 3 && tif.file && xml.file;
    const isValidSelect4 = selectType === 4 && json.file;
    const isValidSelect1 = selectType === 1 && design.file;
    const isValidSelect2 = selectType === 2 && las.file;

    const canUpload =
      (isValidSelect3 || isValidSelect4 || isValidSelect1 || isValidSelect2) &&
      jobSiteId &&
      projectName &&
      validation.isValid;

    setValidation((prev) => ({
      ...prev,
      upload: !canUpload,
      projectName: !canUpload,
    }));
  }, [fileStates, selectType, jobSiteId, projectName, validation.isValid]);

  // File handlers
  const createFileHandler = useCallback(
    (type: keyof typeof fileStates, displayType?: keyof typeof fileStates) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setLoading((prev) => ({ ...prev, file: true }));

        try {
          updateFileState(type, file);
          if (displayType && displayType !== type) {
            updateFileState(displayType, file);
          }
        } catch (error) {
          console.error("File handling error:", error);
        } finally {
          setLoading((prev) => ({ ...prev, file: false }));
        }
      },
    [updateFileState]
  );

  const handleTifFileChange = createFileHandler("tif");
  const handleXmlFileChange = createFileHandler("xml");
  const handleJsonFileChange = createFileHandler("json");
  const handleDesignFileChange = createFileHandler("design");
  const handleLasFileChange = createFileHandler("las");

  const validateFiles = ({
    fileStates,
    selectType,
    t,
  }: {
    fileStates: any;
    selectType: number;
    t: (key: string) => string;
  }) => {
    const { design, las, tif, xml, json } = fileStates;

    if (selectType === 3) {
      if (!tif.file || !xml.file) {
        throw new Error(t("FILE_NOT_EXISTS"));
      }
    } else if (selectType === 4) {
      if (!json.file) {
        throw new Error(t("FILE_NOT_EXISTS"));
      }
    } else if (selectType === 1) {
      if (!design.file) {
        throw new Error(t("FILE_NOT_EXISTS"));
      }
    } else if (selectType === 2) {
      if (!las.file) {
        throw new Error(t("FILE_NOT_EXISTS"));
      }
    }
  };

  const createFormData = async ({
    fileStates,
    selectType,
    projectName,
    jobSiteId,
    color,
    timeStamp,
    useArmarker,
  }: {
    fileStates: any;
    selectType: number;
    projectName: string;
    jobSiteId: string;
    color: string;
    timeStamp: number;
    useArmarker: string;
  }): Promise<FormData> => {
    const data = new FormData();
    const { design, las, tif, xml, json } = fileStates;

    if (selectType === 3) {
      const zip = new JSZip();
      zip.file(tif.file.name, tif.file);
      zip.file(xml.file.name, xml.file);
      const zipBlob = await zip.generateAsync({ type: "blob" });
      const zipFile = new File([zipBlob], "heat_map.zip");
      data.append("uploadFile", zipFile);
    } else if (selectType === 4) {
      data.append("uploadFile", json.file);
    } else if (selectType === 1) {
      data.append("uploadFile", design.file);
    } else if (selectType === 2) {
      data.append("uploadFile", las.file);
    }

    data.append("projectName", projectName);
    data.append("jobsiteId", jobSiteId);
    data.append("ARColor", color);
    data.append("timeStamp", new Date(timeStamp || Date.now()).toISOString());
    data.append("useArmarker", useArmarker);
    data.append("timezone", `${new Date().getTimezoneOffset() * -1}`);

    return data;
  };

  const uploadFile = useCallback(async () => {
    setProgressDialog(true);
    setOpenDialog?.(false);
    resetFileStates();
    setLoading((prev) => ({ ...prev, upload: true }));

    if (!fileStates) {
      setErrorMsg(t("FILE_NOT_EXISTS"));
      setErrorSnackbar(true);
      setProgressDialog(false);
      return;
    }

    try {
      // Step 1: Validate
      validateFiles({ fileStates, selectType, t });

      // Step 2: Create FormData
      const formData = await createFormData({
        fileStates,
        selectType,
        projectName,
        jobSiteId,
        color: colorPicker.color,
        timeStamp,
        useArmarker,
      });

      // Step 3: Upload
      await reUploadARFile({
        projectID,
        formData,
        onProgress: (percent) => setUpLoadingPercent(percent),
      });

      setUploadResult(true);
      setProgressDialog(false);
    } catch (error: any) {
      console.error("Upload error:", error);

      if (error?.response?.status === 504) {
        setOpenWarning(true);
        setMsgWarning(t("SERVER_TIME_OUT"));
      } else {
        setErrorSnackbar(true);
        setErrorMsg(t("UPLOAD_FAILED"));
      }

      setLoading((prev) => ({ ...prev, upload: false }));
      setProgressDialog(false);
    }
  }, [
    fileStates,
    selectType,
    projectName,
    jobSiteId,
    colorPicker.color,
    timeStamp,
    useArmarker,
    projectID,
    t,
  ]);

  // Color picker handlers
  const handleColorChange = useCallback((newColor: ColorResult) => {
    const alpha = newColor.rgb.a || 0;
    const alphaHex =
      alpha === 0
        ? "00"
        : Math.round(255 * alpha)
            .toString(16)
            .padStart(2, "0");
    const hexCode = `${newColor.hex}${alphaHex}`;

    setColorPicker((prev) => ({ ...prev, color: hexCode }));
  }, []);

  const handleColorPickerOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setColorPicker((prev) => ({
        ...prev,
        anchorEl: event.currentTarget,
        open: true,
      }));
    },
    []
  );

  const handleColorPickerClose = useCallback(() => {
    setColorPicker((prev) => ({ ...prev, open: false }));
  }, []);

  // Dialog handlers
  const resetFileStates = useCallback(() => {
    setFileStates({
      design: { file: undefined, fileName: "", fileSize: "", selected: false },
      las: { file: undefined, fileName: "", fileSize: "", selected: false },
      tif: { file: undefined, fileName: "", fileSize: "", selected: false },
      xml: { file: undefined, fileName: "", fileSize: "", selected: false },
      json: { file: undefined, fileName: "", fileSize: "", selected: false },
    });
  }, []);

  const handleOpen = useCallback(async () => {
    setValidation((prev) => ({ ...prev, isValid: true }));
  }, []);

  const handleClose = useCallback(() => {
    setOpenDialog?.(false);
    resetFileStates();

    const scrollY = document.body.style.top;
    document.body.style.cssText = "";
    window.scrollTo(0, parseInt(scrollY || "0", 10) * -1);
  }, [setOpenDialog, resetFileStates]);

  // Effects
  useEffect(() => {
    validateUpload();
  }, [validateUpload]);

  useEffect(() => {
    resetFileStates();
  }, [selectType, resetFileStates]);

  useEffect(() => {
    if (openDialog) {
      handleOpen();
    }
  }, [openDialog, handleOpen]);

  useEffect(() => {
    const handleFocus = () => {
      if (!openDialog) {
        document.body.style.cssText = "";
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [openDialog]);

  // Styled components
  const CustomCancelButton = styled(Button)({
    background: theme.palette.base.modal,
    color: theme.palette.base.contrast,
    boxShadow: "none",
    "&:hover": {
      background: theme.palette.base.modal,
      color: theme.palette.base.contrast,
      boxShadow: "none",
    },
  });

  // Style objects
  const styles = {
    customColor: { color: theme.palette.base.contrast },
    customBackground: { background: theme.palette.base.modal },
    customMainColor: {
      background: theme.palette.primary.main,
      boxShadow: "none",
    },
    buttonStyle: {
      backgroundColor: "transparent",
      boxShadow: "none",
      "&:hover": {
        background: "none",
        boxShadow: "none",
      },
    },
    circleStyle: {
      backgroundColor: colorPicker.color,
      borderRadius: "50%",
      width: 35,
      height: 35,
    },
    borderStyle: {
      border: `2px solid ${theme.palette.base.contrast}`,
      borderRadius: "50%",
      width: "100%",
      height: "100%",
    },
  };

  // Render helpers
  const renderFileInfo = (fileState: FileState) => {
    if (!fileState.selected) return null;

    return (
      <DialogContentText
        style={styles.customColor}
        className="upload-file7 upload-file10"
      >
        <FolderZip
          style={styles.customColor}
          sx={{ width: "60px", height: "60px" }}
        />
        <div id="upload-file11">
          <p id="upload-file12">{fileState.fileName}</p>
          <p id="upload-file13">{fileState.fileSize}</p>
        </div>
      </DialogContentText>
    );
  };

  const renderFileUploadButton = (
    accept: string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void,
    hasFile: boolean,
    marginTop = "10px",
    marginBottom = "15px"
  ) => (
    <LoadingButton
      style={{
        ...styles.customMainColor,
        marginTop,
        marginBottom,
      }}
      size="small"
      variant="contained"
      component="label"
      loading={loading.file}
      className="loading-white upload-file6"
    >
      {hasFile ? t("FILE_CHANGE") : t("FILE_SELECT")}
      <input type="file" accept={accept} hidden onChange={onChange} />
    </LoadingButton>
  );

  const renderColorPicker = () => {
    if (!isXML(fileStates.design.fileName)) return null;

    return (
      <>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <p style={{ marginBottom: 0 }}>{t("AR_DATA_COLOR")}</p>
          <Button
            variant="contained"
            sx={styles.buttonStyle}
            onClick={handleColorPickerOpen}
          >
            <Box sx={styles.circleStyle}>
              <Box sx={styles.borderStyle} />
            </Box>
          </Button>
        </div>

        <Popover
          open={colorPicker.open}
          anchorEl={colorPicker.anchorEl}
          onClose={handleColorPickerClose}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "center", horizontal: "left" }}
          PaperProps={{
            style: { background: "transparent", boxShadow: "none" },
          }}
        >
          <div style={{ marginLeft: 40 }}>
            <SketchPicker
              color={colorPicker.color}
              onChangeComplete={handleColorChange}
              disableAlpha={false}
            />
          </div>
        </Popover>
      </>
    );
  };

  // Main render
  return (
    <>
      {/* File upload section */}
      {selectType !== 3 && (
        <DialogContentText style={styles.customColor} className="upload-file7">
          {t("PLEASE_FILE_SELECT")}
        </DialogContentText>
      )}

      {selectType === 3 ? (
        // TIF and XML files
        <>
          <div>
            <DialogContentText
              style={styles.customColor}
              className="upload-file7"
            >
              {t("PLEASE_FILE_TIFF_SELECT")}
            </DialogContentText>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              {renderFileUploadButton(
                FILE_ACCEPTS[3],
                handleTifFileChange,
                !!fileStates.tif.file
              )}
              {renderFileInfo(fileStates.tif)}
            </div>
          </div>

          <div>
            <DialogContentText
              style={styles.customColor}
              className="upload-file7"
            >
              {t("PLEASE_FILE_XML_SELECT")}
            </DialogContentText>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              {renderFileUploadButton(
                FILE_ACCEPTS.xml,
                handleXmlFileChange,
                !!fileStates.xml.file
              )}
              {renderFileInfo(fileStates.xml)}
            </div>
          </div>
        </>
      ) : selectType === 4 ? (
        // JSON file
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {renderFileUploadButton(
            FILE_ACCEPTS[4],
            handleJsonFileChange,
            !!fileStates.json.file
          )}
          {renderFileInfo(fileStates.json)}
        </div>
      ) : selectType === 1 ? (
        // design file
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {renderFileUploadButton(
            FILE_ACCEPTS[1] || "",
            handleDesignFileChange,
            fileStates.design.selected
          )}
          {renderFileInfo(fileStates.design)}
        </div>
      ) : (
        // las file
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          {renderFileUploadButton(
            FILE_ACCEPTS[2] || "",
            handleLasFileChange,
            fileStates.las.selected
          )}
          {renderFileInfo(fileStates.las)}
        </div>
      )}

      {/* Color picker */}
      {renderColorPicker()}

      {/* Dialog actions */}
      <DialogActions style={{ marginTop: "10px", ...styles.customBackground }}>
        <CustomCancelButton
          size="small"
          variant="contained"
          onClick={handleClose}
          id="upload-file19"
        >
          {t("CANCEL")}
        </CustomCancelButton>

        <LoadingButton
          className={`loading-white upload-check-com ${
            validation.upload ? "upload-check1" : "upload-check2"
          }`}
          size="small"
          variant="contained"
          disabled={validation.upload || loading.file || validation.projectName}
          loading={loading.upload}
          onClick={uploadFile}
          style={
            validation.upload ? { boxShadow: "none" } : styles.customMainColor
          }
        >
          {t("UPLOAD")}
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default UploadARDataDialog;
